import {combine, createEffect, guard, sample} from 'effector';
import i18next from 'i18next';
import _ from 'lodash';
import {toast} from 'react-toastify';

import {RestError, showRestError} from 'core/errors';
import {history} from 'core/history';
import {ROUTE} from 'core/routes';

import {
    batchNumberModal$,
    codes$,
    codesChanged,
    commissioningForm$,
    commissioningFormChanged,
    createCommissioningDocument,
    createDropOutDocument,
    createMovingDocument,
    createUtilisationDocument,
    data$,
    dataChanged,
    DocumentCreatePreviewGate,
    DocumentsCreateGate,
    dropOutForm$,
    dropOutFormChanged,
    fetchGtinInfoFx,
    file$,
    fileChanged,
    gtins$,
    isUtilisationGroup$,
    lastCreatedDocumentId$,
    movingForm$,
    movingFormChanged,
    sendCommissioningFx,
    sendDropOutFx,
    sendMovingFx,
    sendUPDFx,
    sendUtilisationFx,
    setLastCreatedDocumentId,
    showBatchNumberModal,
    step$,
    stepChanged,
    UPDString$,
    UPDStringChanged,
    utilisationForm$,
    utilisationFormChanged,
    utilisationGroupChanged,
} from './index';

data$
    .on(dataChanged, (state, payload) => {
        if (!payload) return [];
        const newState = _.cloneDeep(state);
        const id = newState.findIndex((s) => s.identityCode === payload.identityCode);
        if (id === -1) return [...newState, payload];
        newState.splice(id, 1);
        return newState;
    })
    .reset([
        DocumentsCreateGate.close,
        dropOutFormChanged,
        commissioningFormChanged,
        movingFormChanged,
    ]);
dropOutForm$.on(dropOutFormChanged, (_, payload) => payload).reset(DocumentsCreateGate.close);
utilisationForm$
    .on(utilisationFormChanged, (state, payload) => ({...state, ...payload}))
    .reset(DocumentsCreateGate.close);
isUtilisationGroup$
    .on(utilisationGroupChanged, (state, payload) => payload)
    .reset(DocumentsCreateGate.close);
commissioningForm$
    .on(commissioningFormChanged, (_, payload) => payload)
    .reset(DocumentsCreateGate.close);
movingForm$.on(movingFormChanged, (_, payload) => payload).reset(DocumentsCreateGate.close);
step$.on(stepChanged, (_, payload) => payload).reset(DocumentsCreateGate.close);
file$.on(fileChanged, (_, payload) => payload).reset(DocumentsCreateGate.close);
codes$.on(codesChanged, (_, payload) => payload).reset(DocumentsCreateGate.close);
gtins$.on(fetchGtinInfoFx.done, (_, payload) => payload.result).reset(DocumentsCreateGate.close);
UPDString$.on(UPDStringChanged, (_, payload) => payload).reset(DocumentsCreateGate.close);
lastCreatedDocumentId$.on(setLastCreatedDocumentId, (_, payload) => payload);

batchNumberModal$
    .on(showBatchNumberModal, (state, payload) => payload)
    .reset(DocumentsCreateGate.close);

sendDropOutFx.fail.watch(({error}) => showRestError(error as RestError));

sendDropOutFx.done.watch((data) => {
    history.push(ROUTE.DOCUMENTS_DOCUMENTS);

    const id = data.result?.data?.data?.reportId || '';
    setLastCreatedDocumentId(id);
    toast.success(
        i18next.t('pages.documents.form.documentCreated', {
            number: data.result?.data?.data?.reportId || '',
        }),
        {className: 'Toastify__toast__wide'},
    );
});

sendUtilisationFx.fail.watch(({error}) => showRestError(error as RestError));

sendUtilisationFx.done.watch((data) => {
    // history.push(ROUTE.DOCUMENTS_DOCUMENTS);

    const id = data.result?.data?.data?.reportId || '';
    setLastCreatedDocumentId(id);
    toast.success(i18next.t('pages.documents.form.documentCreated', {number: id}), {
        className: 'Toastify__toast__wide',
    });
});

sendCommissioningFx.fail.watch(({error}) => showRestError(error as RestError));

sendCommissioningFx.done.watch((data) => {
    history.push(ROUTE.DOCUMENTS_DOCUMENTS);

    const id = data.result?.data?.data?.reportId || '';
    setLastCreatedDocumentId(id);
    toast.success(i18next.t('pages.documents.form.documentCreated', {number: id}), {
        className: 'Toastify__toast__wide',
    });
});

sendUPDFx.fail.watch(({error}) => showRestError(error as RestError));

sendUPDFx.done.watch((data) => {
    history.push(ROUTE.DOCUMENTS_DOCUMENTS);

    const id = data.result?.data?.data?.reportId || '';
    setLastCreatedDocumentId(id);
    toast.success(i18next.t('pages.documents.form.documentCreated', {number: id}), {
        className: 'Toastify__toast__wide',
    });
});

sendMovingFx.fail.watch(({error}) => showRestError(error as RestError));

sendMovingFx.done.watch((data) => {
    history.push(ROUTE.DOCUMENTS_MOVING);

    const id = data.result?.data?.data?.reportId || '';
    setLastCreatedDocumentId(id);
    toast.success(i18next.t('pages.documents.form.documentCreated', {number: id}), {
        className: 'Toastify__toast__wide',
    });
});

sample({
    clock: createDropOutDocument,
    source: combine({
        dropOutForm: dropOutForm$,
        scannedCodes: data$.map((p) => p.map((d) => d.identityCode)),
        uploadCodes: codes$,
    }),
    fn: ({dropOutForm, scannedCodes, uploadCodes}) => ({
        ...dropOutForm,
        codes: scannedCodes.length ? scannedCodes : uploadCodes,
        documentId: dropOutForm.documentId ?? '',
    }),
    target: sendDropOutFx,
});

sample({
    clock: createUtilisationDocument,
    source: combine({
        utilisationForm: utilisationForm$,
        scannedCodes: data$.map((p) => p.map((d) => d.identityCode)),
        uploadCodes: codes$,
    }),
    fn: ({
        utilisationForm: {
            factoryId,
            storageLocationId,
            applicationDate,
            manufacturingDate,
            groupMarkingCode,
            batchNumber,
        },
        scannedCodes,
        uploadCodes,
    }) => ({
        factoryId,
        storageLocationId,
        applicationDate,
        manufacturingDate,
        groupMarkingCode,
        batchNumber,
        codes: scannedCodes.length ? scannedCodes : uploadCodes,
    }),
    target: sendUtilisationFx,
});

const historyEventFx = createEffect({
    name: 'historyEventFx',
    handler: () => {
        history.push(ROUTE.DOCUMENTS_DOCUMENTS);
    },
});

const guardManualBatchNumber = guard({
    clock: sendUtilisationFx.done,
    source: utilisationForm$,
    filter: (state) => state.batchNumberSource === 'manual',
});

const guardAutoBatchNumber = guard({
    clock: sendUtilisationFx.done,
    source: utilisationForm$,
    filter: (state) => state.batchNumberSource === 'auto',
});

sample({
    clock: guardManualBatchNumber,
    target: historyEventFx,
});

sample({
    clock: guardAutoBatchNumber,
    fn: () => true,
    target: showBatchNumberModal,
});

guard({
    clock: showBatchNumberModal,
    filter: (state) => !state,
    target: historyEventFx,
});

sample({
    clock: createCommissioningDocument,
    source: combine({
        commissioningForm: commissioningForm$,
        scannedCodes: data$.map((p) => p.map((d) => d.identityCode)),
        uploadCodes: codes$,
    }),
    fn: ({commissioningForm, scannedCodes, uploadCodes}) => ({
        ...commissioningForm,
        codes: scannedCodes.length ? scannedCodes : uploadCodes,
    }),
    target: sendCommissioningFx,
});

sample({
    clock: createMovingDocument,
    source: combine({
        movingForm: movingForm$,
        scannedCodes: data$.map((p) => p.map((d) => d.identityCode)),
        uploadCodes: codes$,
    }),
    fn: ({
        movingForm: {storageLocationFrom, storageLocationTo, note, employeeFullName},
        scannedCodes,
        uploadCodes,
    }) => ({
        storageLocationFrom,
        storageLocationTo,
        note,
        employeeFullName,
        codes: scannedCodes.length ? scannedCodes : uploadCodes,
    }),
    target: sendMovingFx,
});

sample({
    clock: UPDStringChanged,
    target: sendUPDFx,
});

sample({
    clock: DocumentCreatePreviewGate.open,
    source: codes$,
    target: fetchGtinInfoFx,
});
